import { Grid, ListItem, ListItemIcon, ListItemText, Radio } from '@material-ui/core'
import { graphql } from 'gatsby'
import React, { MouseEventHandler, useMemo } from 'react'

import useSiteSettings from '../../../hooks/useSiteSettings'
import useUserCategory from '../../../hooks/useUserCategory'
import { shippingTooltipKeyFromMethod } from '../../../lib/cart/delivery'
import { formatAsCurrencyOrFree } from '../../../utils/priceTools'
import { userCategories, UserCategory } from '../../../utils/userClaims'
import BasicTooltip from '../../global/help/Tooltip/BasicTooltip'
import useStyles from './styles/deliveryMethod'

interface MethodProps {
  carrier_title: string
  method_code: string
  method_title: string
  amount: {
    value: number
  }
}

// User token does not currently include price level - assume from category
const professionalPricingCategories: UserCategory[] = [
  userCategories.practitioner,
  userCategories.otherProfessional,
]

const isPricingProfessional = (userCategory: UserCategory | undefined) =>
  userCategory ? professionalPricingCategories.includes(userCategory) : false

export interface DeliveryMethodProps {
  method: MethodProps
  checked: boolean
  handleSelectMethod(method: MethodProps): MouseEventHandler<HTMLDivElement>
  submitting: boolean
}

const DeliveryMethod: React.FC<DeliveryMethodProps> = ({
  method,
  handleSelectMethod,
  checked,
  submitting,
}) => {
  const { shippingTooltips } = useSiteSettings() ?? {}
  const { userCategory } = useUserCategory()
  const classes = useStyles()

  const tooltipKey = useMemo(
    () => shippingTooltipKeyFromMethod(isPricingProfessional(userCategory))(method),
    [method, userCategory],
  )
  const tooltipContents = useMemo(
    () => (tooltipKey && shippingTooltips ? shippingTooltips[tooltipKey] : undefined),
    [shippingTooltips, tooltipKey],
  )

  // In order to prevent the Tooltip from wrapping to a new line by itself, which looks
  // terrible, we need to ensure that the final word of the method_title is next to
  // the Tooltip inside a span that doesn't allow any line breaks. All words aside from
  // the last one are outside the span to allow other line breaks to flow normally.
  const splitMethodTitle = method.method_title.split(' ')

  return (
    <ListItem
      disabled={submitting}
      divider
      role={undefined}
      dense
      selected={checked}
      button
      onClick={handleSelectMethod(method)}
      classes={{ selected: classes.selected, root: classes.listItem }}
    >
      <Grid alignItems="center" container justify="space-between" wrap="nowrap">
        <Grid alignItems="center" container item wrap="nowrap">
          <Grid item>
            <ListItemIcon className={classes.listItemIcon}>
              <Radio edge="start" checked={checked} />
            </ListItemIcon>
          </Grid>
          <Grid className={classes.carrierInformation} item>
            <ListItemText
              disableTypography
              primary={
                <>
                  <span>
                    {
                      // Trailing space is intentional to prevent this string and the final word
                      // of the method_title from having no space between them.
                      `${method.carrier_title} ${splitMethodTitle
                        .slice(0, splitMethodTitle.length - 1)
                        .join(' ')} `
                    }
                  </span>
                  <span className={classes.noWrap}>
                    {splitMethodTitle[splitMethodTitle.length - 1]}
                    &nbsp;
                    {tooltipContents && (
                      <BasicTooltip contents={tooltipContents} openDirection="top" />
                    )}
                  </span>
                </>
              }
            />
          </Grid>
        </Grid>
        <Grid item>{formatAsCurrencyOrFree({ amount: method.amount.value })}</Grid>
      </Grid>
    </ListItem>
  )
}

export default DeliveryMethod

export const shippingTooltips = graphql`
  fragment shippingTooltips on SanitySiteSettings {
    shippingTooltips {
      expeditedPatient
      expeditedProfessional
      standardPatient
      standardProfessional
    }
  }
`
